import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useConfirm } from './ConfirmDialog';
import './CustomFieldsManager.css';

const same = (a, b) =>
  String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

/**
 * Custom fields, per ticket category.
 *
 * The tabs were the hard-coded list ['general', 'technical', 'billing',
 * 'compliance']. The categories a ticket can actually be filed under come from
 * the categories table - Hardware, Software, Network, Account, Other - and the
 * ticket form asks for a ticket's fields by that name.
 *
 * Those two lists have never had a name in common, so every custom field
 * defined on this page was stored under a heading no ticket could ever ask
 * for. Nobody filling in a ticket has seen one.
 *
 * The tabs are the real categories now, and they follow the Categories editor
 * above: add one and it appears here, remove one and it goes. Fields already
 * stored under a name that is no longer a category are kept as a tab of their
 * own, marked - they are somebody's work, and hiding them would be the same
 * mistake in the other direction.
 */
const CustomFieldsManager = ({ token }) => {
  const [confirm, confirmDialog] = useConfirm();
  const [fields, setFields] = useState([]);
  const [ticketCategories, setTicketCategories] = useState([]);
  const [storedCategories, setStoredCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isAddingField, setIsAddingField] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    field_name: '',
    field_label: '',
    field_type: 'text',
    is_required: false,
    options: ''
  });

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  /**
   * `token` is a leftover, and nothing has set it since staff sign-in moved to
   * Entra: localStorage('token') is never written anywhere in this app, so the
   * prop Settings passes is always null. Authentication is attached by the
   * axios interceptor in lib/apiClient instead, which also knows that a header
   * reading "Bearer null" is not a credential and replaces it.
   *
   * That mattered more than it looks. Every request here was gated on
   * `if (token)`, so on the real site this page fetched NOTHING - it rendered
   * its tabs, said "No custom fields for this category yet" under each of
   * them, and would have said that with a hundred fields in the table. The
   * gates are gone; the header is still sent when there is something real to
   * send, for any caller that does have one.
   */
  // Memoised, and not for tidiness: it is a dependency of the two useCallbacks
  // below, which are dependencies of the effects that fetch. A fresh object
  // every render would make every render a new callback, every new callback a
  // re-run of the effect, and every re-run another render - a request loop
  // that only shows up against a real API.
  const authHeaders = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : undefined), [token]);

  // The real categories, plus the headings fields are already stored under.
  // Both are needed: the first is what the tabs should be, the second is what
  // would otherwise become unreachable.
  const loadCategories = useCallback(async () => {
    const headers = authHeaders;
    const [catsRes, allRes] = await Promise.all([
      axios.get(`${API_BASE}/categories`, { headers }).catch(() => ({ data: [] })),
      axios.get(`${API_BASE}/custom-fields`, { headers }).catch(() => ({ data: [] }))
    ]);
    // Guard the shape on both: .map on a wrapped object throws during render
    // and takes the whole Settings page down with it.
    const cats = Array.isArray(catsRes.data) ? catsRes.data : (catsRes.data?.categories ?? []);
    setTicketCategories(
      cats.map((c) => (typeof c === 'string' ? c : c.name)).filter(Boolean));
    const all = Array.isArray(allRes.data) ? allRes.data : (allRes.data?.custom_fields ?? []);
    setStoredCategories([...new Set(all.map((f) => f.category).filter(Boolean))]);
  }, [authHeaders, API_BASE]);

  const fetchFields = useCallback(async () => {
    if (!selectedCategory) { setFields([]); return; }
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE}/custom-fields`,
        { headers: authHeaders, params: { category: selectedCategory } }
      );
      const data = response.data;
      setFields(Array.isArray(data) ? data : (data?.custom_fields ?? []));
      setError(null);
    } catch (err) {
      console.error('Error fetching custom fields:', err);
      setError('Failed to load custom fields');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, authHeaders, API_BASE]);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  useEffect(() => { fetchFields(); }, [fetchFields]);

  // Memoised because it is a useEffect dependency below: rebuilt every render
  // it would re-run the effect every render, and CI=true turns that warning
  // into a failed build rather than a note.
  const categories = useMemo(() => {
    const orphaned = storedCategories.filter(
      (c) => !ticketCategories.some((t) => same(t, c)));
    return [...ticketCategories, ...orphaned];
  }, [ticketCategories, storedCategories]);

  // A heading that is not a category any more: fields filed under it are
  // stored and can be edited, but no ticket will ever ask for them.
  const isOrphan = (cat) => !ticketCategories.some((t) => same(t, cat));

  useEffect(() => {
    if (!selectedCategory && categories.length > 0) setSelectedCategory(categories[0]);
  }, [selectedCategory, categories]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.field_name || !formData.field_label) {
      setError('Field name and label are required');
      return;
    }
    // With no categories there is no heading to file this under, and the API
    // would answer 400 with a message about a category the form never asked
    // for.
    if (!selectedCategory) {
      setError('Add a ticket category first - a custom field belongs to one');
      return;
    }

    try {
      const headers = authHeaders;
      const payload = {
        ...formData,
        category: selectedCategory,
        options: formData.options ? formData.options.split(',').map(o => o.trim()) : []
      };

      if (editingId) {
        // Update existing field
        await axios.put(
          `${API_BASE}/custom-fields/${editingId}`,
          payload,
          { headers }
        );
        setSuccess('Field updated successfully');
        setEditingId(null);
      } else {
        // Create new field
        await axios.post(
          `${API_BASE}/custom-fields`,
          payload,
          { headers }
        );
        setSuccess('Field created successfully');
      }

      setFormData({
        field_name: '',
        field_label: '',
        field_type: 'text',
        is_required: false,
        options: ''
      });
      setIsAddingField(false);
      await fetchFields();
      // The tab list is partly derived from where fields are stored, so it has
      // to be re-read whenever that changes.
      await loadCategories();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save field');
    }
  };

  const handleEdit = (field) => {
    setEditingId(field.id);
    setFormData({
      field_name: field.field_name,
      field_label: field.field_label,
      field_type: field.field_type,
      is_required: field.is_required,
      options: Array.isArray(field.options) ? field.options.join(', ') : ''
    });
    setIsAddingField(true);
  };

  const handleDelete = async (id) => {
    if (!(await confirm({ title: 'Are you sure you want to delete this field?', confirmLabel: 'Delete', danger: true }))) return;

    try {
      const headers = authHeaders;
      await axios.delete(
        `${API_BASE}/custom-fields/${id}`,
        { headers }
      );
      setSuccess('Field deleted successfully');
      await fetchFields();
      await loadCategories();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to delete field');
    }
  };

  const handleCancel = () => {
    setIsAddingField(false);
    setEditingId(null);
    setFormData({
      field_name: '',
      field_label: '',
      field_type: 'text',
      is_required: false,
      options: ''
    });
  };

  return (
    <div className="custom-fields-manager">
      <div className="cf-header">
        <p className="cf-lead">Choose a category, then add the questions its tickets ask.</p>
        <button
          className="btn-add-field"
          onClick={() => setIsAddingField(true)}
          disabled={isAddingField || !selectedCategory}
        >
          + Add Custom Field
        </button>
      </div>

      <div className="cf-category-tabs">
        {categories.length === 0 && (
          <span className="cf-no-categories">
            No ticket categories are configured. Add one under Categories above
            and it will appear here.
          </span>
        )}
        {categories.map(cat => (
          <button
            key={cat}
            className={`tab ${selectedCategory === cat ? 'active' : ''}`}
            onClick={() => setSelectedCategory(cat)}
          >
            {cat.charAt(0).toUpperCase() + cat.slice(1)}
            {isOrphan(cat) && (
              <span className="cf-tab-warn" title="No longer a ticket category">!</span>
            )}
          </button>
        ))}
      </div>

      {error && <div className="cf-alert error">⚠️ {error}</div>}
      {success && <div className="cf-alert success">✅ {success}</div>}

      {/* Said here rather than left for somebody to discover from a ticket
          form that never shows the field they spent ten minutes defining. */}
      {selectedCategory && isOrphan(selectedCategory) && (
        <div className="cf-alert warn" role="alert">
          <strong>{selectedCategory}</strong> is not a ticket category, so no
          ticket will ever show these fields. Tickets are categorised as{' '}
          {ticketCategories.join(', ') || '(none are configured)'} &mdash;
          either add <strong>{selectedCategory}</strong> under Categories
          above, or recreate these fields under one of those and delete them
          here.
        </div>
      )}

      {isAddingField && (
        <div className="cf-form-section">
          <h3>{editingId ? 'Edit Field' : 'New Custom Field'}</h3>
          <form onSubmit={handleSubmit} className="cf-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="field_name">Field Name *</label>
                <input
                  id="field_name"
                  type="text"
                  name="field_name"
                  value={formData.field_name}
                  onChange={handleInputChange}
                  placeholder="e.g., support_tier"
                  required
                />
                <small>No spaces, use underscores</small>
              </div>

              <div className="form-group">
                <label htmlFor="field_label">Display Label *</label>
                <input
                  id="field_label"
                  type="text"
                  name="field_label"
                  value={formData.field_label}
                  onChange={handleInputChange}
                  placeholder="e.g., Support Tier"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="field_type">Field Type *</label>
                <select
                  id="field_type"
                  name="field_type"
                  value={formData.field_type}
                  onChange={handleInputChange}
                  required
                >
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="textarea">Text Area</option>
                  <option value="select">Dropdown</option>
                  <option value="checkbox">Checkbox</option>
                  <option value="date">Date</option>
                  <option value="email">Email</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="is_required" className="checkbox-label">
                  <input
                    id="is_required"
                    type="checkbox"
                    name="is_required"
                    checked={formData.is_required}
                    onChange={handleInputChange}
                  />
                  Required Field
                </label>
              </div>
            </div>

            {(formData.field_type === 'select') && (
              <div className="form-group">
                <label htmlFor="options">Options (comma-separated)</label>
                <input
                  id="options"
                  type="text"
                  name="options"
                  value={formData.options}
                  onChange={handleInputChange}
                  placeholder="Option 1, Option 2, Option 3"
                />
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingId ? 'Update Field' : 'Create Field'}
              </button>
              <button type="button" className="btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="cf-loading">Loading fields...</div>
      ) : fields.length === 0 ? (
        <div className="cf-empty">
          <p>No custom fields for this category yet.</p>
          <button className="btn-link" onClick={() => setIsAddingField(true)}>
            Create the first one
          </button>
        </div>
      ) : (
        <div className="cf-fields-list">
          {fields.map(field => (
            <div key={field.id} className="cf-field-card">
              <div className="field-header">
                <div className="field-info">
                  <h4 className="field-label">{field.field_label}</h4>
                  <p className="field-name">{field.field_name}</p>
                </div>
                <div className="field-meta">
                  <span className="badge type">{field.field_type}</span>
                  {field.is_required && <span className="badge required">Required</span>}
                </div>
              </div>

              {field.options && field.options.length > 0 && (
                <div className="field-options">
                  <strong>Options:</strong> {field.options.join(', ')}
                </div>
              )}

              <div className="field-actions">
                <button
                  className="btn-edit"
                  onClick={() => handleEdit(field)}
                >
                  Edit
                </button>
                <button
                  className="btn-delete"
                  onClick={() => handleDelete(field.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {confirmDialog}
    </div>
  );
};

export default CustomFieldsManager;
